import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/features/cart/context/CartContext';
import { resolveUnitPrice } from '@/lib/pricing';
import WholesaleNotice from '@/features/products/components/WholesaleNotice';

export default function ProductCard({ product }) {
  const {
    cart,
    canShop,
    addToCart,
    updateQuantity,
    removeFromCart,
  } = useCart();
  const [adding, setAdding] = useState(false);
  const [recentlyAdded, setRecentlyAdded] = useState(false);
  const successTimer = useRef(null);
  const cartItem = cart.find((item) => item.id === product.id);
  const stock = Number(product.stock_quantity) || 0;
  const outOfStock = stock <= 0;
  const lowStock = stock > 0 && stock <= 5;

  useEffect(() => () => window.clearTimeout(successTimer.current), []);

  const stopLinkNavigation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleAdd = async (event) => {
    stopLinkNavigation(event);
    if (adding || outOfStock) return;

    setAdding(true);
    try {
      await addToCart(product, 1);
      setRecentlyAdded(true);
      window.clearTimeout(successTimer.current);
      successTimer.current = window.setTimeout(() => setRecentlyAdded(false), 1000);
    } catch (error) {
      console.error('Unable to add storefront product to cart:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleQuantityChange = (event, nextQuantity) => {
    stopLinkNavigation(event);
    if (nextQuantity < 1) {
      removeFromCart(product.id);
      return;
    }
    if (nextQuantity <= stock) updateQuantity(product.id, nextQuantity);
  };

  return (
    <article className="vendor-product-card">
      <Link className="vendor-product-card-link" to={`/product/${product.id}`}>
        <div className="vendor-product-image-wrap">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name_fa} loading="lazy" />
          ) : (
            <div className="vendor-product-image-placeholder" aria-hidden="true">
              <i className="fas fa-image" />
            </div>
          )}
          {product.wholesale_price != null && product.wholesale_min_quantity != null && (
            <span className="vendor-product-wholesale-badge">
              <i className="fas fa-boxes" aria-hidden="true" />
              عمده از {Number(product.wholesale_min_quantity).toLocaleString('fa-IR')} عدد
            </span>
          )}
          {outOfStock && <span className="vendor-product-stock-badge">ناموجود</span>}
          {recentlyAdded && (
            <span className="vendor-product-success" role="status" aria-label="به سبد اضافه شد">
              <i className="fas fa-check" aria-hidden="true" />
            </span>
          )}
        </div>
        <div className="vendor-product-details">
          <h2>{product.name_fa}</h2>
          {lowStock && (
            <p className="vendor-product-low-stock">
              فقط {stock.toLocaleString('fa-IR')} عدد باقی مانده
            </p>
          )}
          <p className="vendor-product-price">
            {Number(resolveUnitPrice(product, cartItem?.quantity ?? 1)).toLocaleString('fa-IR')}
            <span>تومان</span>
          </p>
        </div>
      </Link>

      <div className="vendor-product-actions">
        {!canShop ? (
          <Link className="vendor-product-view" to={`/product/${product.id}`}>
            <i className="fas fa-eye" aria-hidden="true" /> مشاهده
          </Link>
        ) : cartItem ? (
          <>
            <div className="vendor-product-qty" aria-label={`تعداد ${product.name_fa} در سبد خرید`}>
              <button
                type="button"
                className="vendor-product-qty-btn"
                aria-label="کاهش تعداد"
                onClick={(event) => handleQuantityChange(event, cartItem.quantity - 1)}
              >
                {cartItem.quantity <= 1 ? <i className="fas fa-trash" aria-hidden="true" /> : '−'}
              </button>
              <span className="vendor-product-qty-value">
                {cartItem.quantity.toLocaleString('fa-IR')}
              </span>
              <button
                type="button"
                className="vendor-product-qty-btn"
                aria-label="افزایش تعداد"
                disabled={cartItem.quantity >= stock}
                onClick={(event) => handleQuantityChange(event, cartItem.quantity + 1)}
              >
                +
              </button>
            </div>
            <WholesaleNotice item={product} quantity={cartItem.quantity} />
          </>
        ) : (
          <button
            type="button"
            className="vendor-product-quickadd"
            disabled={outOfStock || adding}
            onClick={handleAdd}
          >
            <i className={`fas ${outOfStock ? 'fa-ban' : 'fa-cart-plus'}`} aria-hidden="true" />
            {outOfStock ? 'ناموجود' : adding ? 'در حال افزودن...' : 'افزودن به سبد'}
          </button>
        )}
      </div>
    </article>
  );
}
